/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import merge from 'lodash.merge';
import {getDeviceByPuls} from '@ion/core/src/services/Connect';
import {Device} from '@ion/core/src/types/entities';
import {QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseDeviceResult = UseDefaultQueryResult & {
  device: Device;
  refresh: Function;
};

export interface UseDeviceParams {
  id: number,
  device?: Device,
  includeSCISettings?: boolean;
};

/**
 * Retrieves the device details for the given device id.
 *
 * @param id of the device to retrieve details for
 * @param enabled if the device is enabled / disabled
 * @param staleTime how long the query should hold onto details before re-fetching
 * @returns a query result
 */
export const useDevice = (
  {
    id,
    device,
    includeSCISettings = false
  }: UseDeviceParams,
  enabled = true,
  staleTime = getStaleTime(1, false, false)
): UseDeviceResult => {
  const queryClient = useQueryClient();
  const query = useQuery([QueryKey.DEVICE_GET, id], async() => {
    const results = await getDeviceByPuls(id, includeSCISettings);
    return results;
  }, {
    initialData : device,
    staleTime   : staleTime as number,
    enabled
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries([QueryKey.DEVICE_GET, id]);
  }, [id]);

  useEffect(() => {
    if (enabled && !query.data) {
      query.refetch();
    }
  }, [enabled, query]);

  return createQueryResult(query, {
    device : merge(device || {}, query.data || {}),
    refresh
  }) as UseDeviceResult;
};

export default useDevice;
